import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import { withAuth0, useAuth0 } from '@auth0/auth0-react';

import { setCountry, selectCountry, selectIsInitialized } from '../../../store/slices/appSlice';
import './headerbar.css';

const HeaderBar = () => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const country = useSelector(selectCountry);
  const isInitialized = useSelector(selectIsInitialized);
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { loginWithRedirect, logout, user, isAuthenticated, isLoading } = useAuth0();

  // Only show authenticated UI once Auth0 finished loading AND the profile
  // fetch confirmed the session is valid. Prevents a flash of Dashboard/Logout
  // on cold load when Auth0's localStorage cache says "authenticated" but the
  // refresh token has actually expired.
  const isAuthed = isAuthenticated && isInitialized;
  const isCheckingAuth = isLoading || (isAuthenticated && !isInitialized);

  // Set Canada as default country on mount
  useEffect(() => {
    // Always use Canada as the default country
    if (!country || country !== 'Canada') {
      dispatch(setCountry('Canada'));
      Cookies.set('country', 'Canada', { expires: 7 });
      i18n.changeLanguage('en-CA');
    }
  }, [dispatch, country, i18n]);

  const handleCountryChange = (event) => {
    dispatch(setCountry(event.target.value));
    Cookies.set('country', event.target.value, { expires: 7 });
    i18n.changeLanguage('en-CA'); // Always use Canadian English
  };

  return (
    <header className="modern-header">
      <div className="header-container">
        {/* Logo */}
        <div className="header-logo">
          <a href='/'>
            <img src='/adgro-logo-color.png' alt='adgro' className="logo-image" />
          </a>
        </div>

        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          {!isCheckingAuth && (
            isAuthed ? (
              <a href='/dashboard' className="nav-link">{t('header.nav.dashboard')}</a>
            ) : (
              <>
                <a href='/' className="nav-link">{t('header.nav.home')}</a>
                <a href='/#why-choose-us' className="nav-link">{t('header.nav.features')}</a>
                <a href='/#pricing' className="nav-link">{t('header.nav.pricing')}</a>
              </>
            )
          )}
          {!isCheckingAuth && (
            <a href='/#signup-form' className="nav-link">{t('header.nav.contact')}</a>
          )}
        </nav>

        {/* CTA Buttons */}
        <div className="header-cta">
          {!isCheckingAuth && !isAuthed && (
            <Select
              className="country-select"
              value={country || 'Canada'}
              onChange={handleCountryChange}
              variant="standard"
              disableUnderline
            >
              <MenuItem value={'Canada'}><img src="/ca.png" alt="Canada flag" style={{width: '20px', marginRight: '8px'}} />CA</MenuItem>
            </Select>
          )}
          {!isCheckingAuth && (
            isAuthed ? (
              <>
                <span className="welcome-text">
                  {t('header.nav.welcome')}<strong>{user.name}</strong>!
                </span>
                <button className="btn-secondary" onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
                  {t('header.nav.log-out')}
                </button>
              </>
            ) : (
              <>
                <button className="btn-ghost" onClick={() => loginWithRedirect({ redirectUri: `${window.location.origin}/dashboard` })}>
                  {t('header.nav.log-in')}
                </button>
                <button className="btn-primary" onClick={() => loginWithRedirect({ redirectUri: `${window.location.origin}/dashboard` })}>
                  {t('header.nav.sign-up')}
                </button>
              </>
            )
          )}
        </div>

        {/* Mobile Menu Button */}
        <button className="mobile-menu-btn" onClick={() => setMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && !isCheckingAuth && (
        <div className="mobile-menu">
          <nav className="mobile-nav">
            {isAuthed ? (
              <a href='/dashboard' className="mobile-nav-link">{t('header.nav.dashboard')}</a>
            ) : (
              <>
                <a href='/' className="mobile-nav-link">{t('header.nav.home')}</a>
                <a href='/#why-choose-us' className="mobile-nav-link">{t('header.nav.features')}</a>
                <a href='/#pricing' className="mobile-nav-link">{t('header.nav.pricing')}</a>
              </>
            )}
            <a href='/#signup-form' className="mobile-nav-link">{t('header.nav.contact')}</a>

            <div className="mobile-cta">
              {!isAuthed && (
                <Select
                  className="country-select-mobile"
                  value={country || 'Canada'}
                  onChange={handleCountryChange}
                  variant="standard"
                  disableUnderline
                >
                  <MenuItem value={'Canada'}><img src="/ca.png" alt="Canada flag" style={{width: '20px', marginRight: '8px'}} />CA</MenuItem>
                </Select>
              )}
              {isAuthed ? (
                <button className="btn-secondary-mobile" onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
                  {t('header.nav.log-out')}
                </button>
              ) : (
                <>
                  <button className="btn-outline-mobile" onClick={() => loginWithRedirect({ redirectUri: `${window.location.origin}/dashboard` })}>
                    {t('header.nav.log-in')}
                  </button>
                  <button className="btn-primary-mobile" onClick={() => loginWithRedirect({ redirectUri: `${window.location.origin}/dashboard` })}>
                    {t('header.nav.sign-up')}
                  </button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

HeaderBar.propTypes = {
  user: PropTypes.shape({}),
  auth0: PropTypes.shape({
    loginWithRedirect: PropTypes.func.isRequired,
    logout: PropTypes.func.isRequired,
  }).isRequired,
};

HeaderBar.defaultProps = {
  user: null,
};

export default withAuth0(HeaderBar);
